import { Link } from "react-router-dom";
import { useMemo } from "react";
import { ArrowRight, Truck, Clock, CreditCard, Phone, Star, Sparkles, Award, Users, Heart, TrendingUp, Flame, PartyPopper, Microwave, Boxes, Scissors, MapPin, History } from "lucide-react";
import { useCart } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import useRecentlyViewed from "@/hooks/useRecentlyViewed";

const DELIVERY_ZONES = [
  {
    id: 1,
    name: "Zone 1",
    fee: 7.00,
    postcodes: [1, 2, 3, 4, 8, 9, 11, 12, 13, 14, 15, 16],
  },
  {
    id: 2,
    name: "Zone 2",
    fee: 7.50,
    postcodes: [5, 6, 7, 10, 17, 18, 19, 20, 21, 22, 23, 24, 30, 31],
  },
  {
    id: 3,
    name: "Zone 3",
    fee: 8.00,
    postcodes: [25, 26, 27, 28, 29, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 50],
  },
];

export default function HomePage() {
  const { categories, products, loading } = useCart();
  const { ids: recentlyViewedIds } = useRecentlyViewed();

  const specials = products.filter((p) => p.is_special);

  // Memoise expensive scans across the full catalog (1,200+ items) so they only
  // re-run when the products array reference actually changes.
  const bestSellers = useMemo(
    () => products.filter((p) => p.is_bestseller).slice(0, 8),
    [products]
  );

  // Recently viewed — map stored IDs (order preserved) to live product objects.
  // Cap at 8 to keep the strip a single scroll of cards on desktop.
  const recentlyViewed = useMemo(() => {
    if (!recentlyViewedIds.length || !products.length) return [];
    const byId = new Map(products.map((p) => [p.id, p]));
    return recentlyViewedIds
      .map((id) => byId.get(id))
      .filter(Boolean)
      .slice(0, 8);
  }, [recentlyViewedIds, products]);

  // Curate "Party & Catering Essentials" — foil platters/dishes, glassware, candles,
  // disposable cups/plates, scouring pads etc. Filtered by name keyword on the household
  // catalog so it stays fresh as new items are added.
  const partyEssentialsRegex = /\b(foil|platter|tumbler|tumblers|glass|glasses|flute|candle|candles|napkin|napkins|paper plate|disposable|cup|cups|cutlery)\b/i;
  const partyEssentials = useMemo(
    () =>
      products
        .filter((p) => p.is_available !== false && partyEssentialsRegex.test(p.name))
        .slice(0, 8),
    [products]
  );

  // Ready Meals shelf — surfaces the Ready Meals category on the homepage so
  // on-the-go shoppers don't have to dig into the categories dropdown.
  const readyMealsCategoryId = useMemo(
    () => categories.find((c) => c.slug === "ready-meals")?.id,
    [categories]
  );
  const readyMeals = useMemo(
    () =>
      readyMealsCategoryId
        ? products
            .filter((p) => p.category_id === readyMealsCategoryId && p.is_available !== false)
            .slice(0, 8)
        : [],
    [products, readyMealsCategoryId]
  );

  // Hair Care Essentials — auto-pulls products from the dedicated Hair category.
  // Targets the African / Caribbean customer base.
  const hairCategoryId = useMemo(
    () => categories.find((c) => c.slug === "hair")?.id,
    [categories]
  );
  const hairCare = useMemo(
    () =>
      hairCategoryId
        ? products
            .filter(
              (p) => p.category_id === hairCategoryId && p.is_available !== false
            )
            .slice(0, 8)
        : [],
    [products, hairCategoryId]
  );

  // Wholesale & Bulk shelf — surfaces all the box / bulk SKUs across Frozen Fish,
  // Poultry, Meat AND drinks bulk cases. Balanced across categories so customers
  // see variety, not a wall of just one product type.
  const wholesaleRegex = /\b(box|bulk|wholesale|carton|case|crate|\(per kg\))\b/i;
  const wholesale = useMemo(
    () => {
      const all = products.filter(
        (p) => p.is_available !== false && wholesaleRegex.test(p.name)
      );
      // Group by category, then take up to 2 from each so variety wins.
      const byCat = {};
      for (const p of all) {
        if (!byCat[p.category_id]) byCat[p.category_id] = [];
        byCat[p.category_id].push(p);
      }
      // Round-robin across categories until we have 8 items.
      const picked = [];
      const queues = Object.values(byCat);
      let safety = 0;
      while (picked.length < 8 && queues.some((q) => q.length) && safety++ < 50) {
        for (const q of queues) {
          if (picked.length >= 8) break;
          if (q.length) picked.push(q.shift());
        }
      }
      return picked;
    },
    [products]
  );

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_grocery-pickup-1/artifacts/7rx67fk8_3E76B642-946E-471C-B403-A39EF9D1D736.webp"
            alt="Fresh produce"
            className="w-full h-full object-cover object-center"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-16 sm:pb-20">
          <div className="max-w-2xl animate-slide-in-up">
            <Badge className="bg-emerald-600 text-white mb-4">Click & Collect Available</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight hero-title">
              Fresh Halal Meat & Quality Groceries
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-lg hero-copy">
              Your trusted local superstore in Manchester. Order online and collect in-store at your convenience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button 
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-14 text-lg rounded-full"
                  data-testid="order-online-btn"
                >
                  Order Online <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/products?special=true">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 px-8 h-14 text-lg rounded-full"
                  data-testid="weekly-specials-btn"
                >
                  Weekly Specials
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Saturday Delivery Banner */}
      <section className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-600 py-4 relative overflow-hidden" data-testid="saturday-delivery-banner">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 50%, white 1px, transparent 1px)", backgroundSize: "40px 40px"}}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-white">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <Truck className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="text-sm sm:text-base font-bold leading-tight">
                  Now Delivering Across Manchester Every Saturday
                </p>
                <p className="text-xs sm:text-sm text-emerald-50">
                  9am – 6pm · M postcodes · From £7 · £50 minimum order
                </p>
              </div>
            </div>
            <Link
              to="/products"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold text-sm px-4 py-2 rounded-full transition-colors"
              data-testid="delivery-banner-shop-btn"
            >
              Shop Now
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Manchester Delivery Zones */}
      <section className="py-14 sm:py-16 bg-slate-50" data-testid="delivery-zones-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <MapPin className="h-5 w-5 text-emerald-700" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Manchester Delivery Zones
            </h2>
          </div>
          <p className="text-slate-600 mb-8 max-w-2xl">
            M-postcode prefixes mapped to delivery zones and fees. Enter your postcode
            at checkout for an instant delivery quote.
          </p>

          <div className="grid gap-5 md:grid-cols-3">
            {DELIVERY_ZONES.map((zone) => (
              <div
                key={zone.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                data-testid={`delivery-zone-${zone.id}`}
              >
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-5 text-white">
                  <p className="text-sm font-medium text-emerald-100">{zone.name}</p>
                  <p className="text-3xl sm:text-4xl font-bold mt-1">
                    £{zone.fee.toFixed(2)}
                  </p>
                  <p className="text-sm text-emerald-100 mt-1">
                    {zone.postcodes.length} postcodes
                  </p>
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap gap-1.5">
                    {zone.postcodes.map((n) => (
                      <span
                        key={n}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium tabular-nums text-slate-700 bg-slate-100 rounded-full"
                      >
                        M{n}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-slate-500 mt-6">
            Delivery available Saturdays only, 9am – 6pm. £50 minimum order. Outside
            Manchester (non-M postcodes) not currently served.
          </p>
        </div>
      </section>

      {/* Spices Announcement Banner */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 text-white">
            <Sparkles className="h-5 w-5 animate-pulse" />
            <p className="text-sm sm:text-base font-medium text-center">
              Explore Our Wide Range of Spices — Now Available in Store!
            </p>
            <Sparkles className="h-5 w-5 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { id: 'click-collect', icon: Truck, title: "Click & Collect or Delivery", desc: "Pickup in-store or Sat delivery across Manchester" },
              { id: 'open-daily', icon: Clock, title: "Open Daily", desc: "Mon-Sat 8AM-10PM, Sun 8AM-8PM" },
              { id: 'pay-your-way', icon: CreditCard, title: "Pay Your Way", desc: "Card or pay at pickup" },
              { id: 'call-to-order', icon: Phone, title: "Call to Order", desc: "07404 133860" },
            ].map((feature) => (
              <div 
                key={feature.id} 
                className="flex flex-col items-center text-center p-4 sm:p-6 bg-white rounded-2xl shadow-sm"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                  <feature.icon className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-slate-900 text-sm sm:text-base">{feature.title}</h3>
                <p className="text-slate-500 text-xs sm:text-sm mt-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Shop by Category</h2>
            <p className="text-slate-600">Browse our range of fresh halal meat, drinks, and groceries</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-64 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/products/${category.slug}`}
                  className="group relative h-64 rounded-2xl overflow-hidden category-card bg-slate-900 flex items-center justify-center"
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image_url}
                    alt={category.name}
                    className="max-w-[85%] max-h-[60%] object-contain rounded-lg shadow-lg transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">{category.name}</h3>
                    <p className="text-white/80 text-sm">{category.description}</p>
                    <span className="inline-flex items-center text-emerald-400 font-medium mt-3 text-sm group-hover:gap-2 transition-all">
                      Shop Now <ArrowRight className="h-4 w-4 ml-1" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sticky in-page chip nav — quick jump between curated shelves.
          Sticky inside <main>, sits below the global Header (top-16 = 4rem). */}
      <nav
        className="sticky top-16 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200"
        data-testid="homepage-chip-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-3 no-scrollbar">
            {[
              { id: "recently-viewed", label: "Recently Viewed", show: recentlyViewed.length > 0 },
              { id: "best-sellers", label: "Best Sellers", show: bestSellers.length > 0 },
              { id: "ready-meals", label: "Ready Meals", show: readyMeals.length > 0 },
              { id: "party-essentials", label: "Party", show: partyEssentials.length > 0 },
              { id: "haircare", label: "Hair Care", show: hairCare.length > 0 },
              { id: "wholesale", label: "Wholesale", show: wholesale.length > 0 },
            ]
              .filter((c) => c.show)
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    const el = document.getElementById(c.id);
                    if (el) {
                      const y = el.getBoundingClientRect().top + window.scrollY - 80;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }
                  }}
                  className="shrink-0 whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium border border-slate-200 text-slate-700 hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                  data-testid={`chip-nav-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
          </div>
        </div>
      </nav>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && (
        <section
          id="recently-viewed"
          className="py-16 sm:py-20 bg-slate-50"
          data-testid="recently-viewed-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <History className="h-4 w-4 text-indigo-600" />
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700">Pick Up Where You Left Off</Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Recently Viewed</h2>
                <p className="text-slate-500 mt-1">Jump straight back to the products you were browsing</p>
              </div>
              <Link to="/products">
                <Button
                  variant="outline"
                  className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 rounded-full"
                  data-testid="view-all-recently-viewed-btn"
                >
                  Keep Shopping <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentlyViewed.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Best Sellers */}
      <section id="best-sellers" className="py-16 sm:py-20 bg-white" data-testid="best-sellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Flame className="h-4 w-4 text-orange-600" />
                </div>
                <Badge className="bg-orange-100 text-orange-700">Customers Love These</Badge>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Best Sellers</h2>
              <p className="text-slate-500 mt-1">Our most popular products picked by shoppers like you</p>
            </div>
            <Link to="/products">
              <Button variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50 rounded-full" data-testid="view-all-bestsellers-btn">
                View All Products <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-80 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bestSellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ready Meals */}
      {readyMeals.length > 0 && (
        <section className="py-16 sm:py-20 bg-gradient-to-br from-red-50 via-white to-orange-50" data-testid="ready-meals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <Microwave className="h-4 w-4 text-red-600" />
                  </div>
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Dinner sorted in 5 minutes</Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Ready Meals</h2>
                <p className="text-slate-500 mt-1">Home-cooked Nigerian comfort food — heat, eat, enjoy</p>
              </div>

              {/* 3-tile collage hint: comfort • home-cooked • microwave-ready */}
              <div className="hidden md:flex items-center gap-6" data-testid="ready-meals-collage">
                {[
                  { src: "/api/static/products/rm_tile_comfort_ae304a3e.png", label: "Comfort" },
                  { src: "/api/static/products/rm_tile_homecooked_a9024008.png", label: "Home-cooked" },
                  { src: "/api/static/products/rm_tile_microwave_e85340dd.png", label: "Ready in 5 min" },
                ].map((t, i) => (
                  <div key={t.label} className="flex flex-col items-center gap-1.5" style={{ marginLeft: i === 0 ? 0 : "-1.25rem" }}>
                    <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full overflow-hidden ring-4 ring-white shadow-lg shadow-red-100">
                      <img src={t.src} alt={t.label} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="text-[10px] lg:text-xs font-medium text-slate-600 tracking-wide uppercase">{t.label}</span>
                  </div>
                ))}
              </div>

              <Link to="/products/ready-meals" className="md:ml-2">
                <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50 rounded-full" data-testid="view-all-ready-meals-btn">
                  Shop Ready Meals <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {readyMeals.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Party & Catering Essentials */}
      {partyEssentials.length > 0 && (
        <section id="party-essentials" className="py-16 sm:py-20 bg-gradient-to-br from-purple-50 via-white to-amber-50" data-testid="party-essentials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <PartyPopper className="h-4 w-4 text-purple-600" />
                  </div>
                  <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Hosting? We've got you</Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Party &amp; Catering Essentials</h2>
                <p className="text-slate-500 mt-1">Foil platters, glassware, candles &amp; serving must-haves for every gathering</p>
              </div>
              <Link to="/products/household">
                <Button variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-full" data-testid="view-all-party-essentials-btn">
                  Shop Household <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {partyEssentials.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Hair Care Essentials */}
      {hairCare.length > 0 && (
        <section id="haircare" className="py-16 sm:py-20 bg-gradient-to-br from-pink-50 via-white to-rose-50" data-testid="haircare-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                    <Scissors className="h-4 w-4 text-rose-600" />
                  </div>
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Trusted Afro-Caribbean brands</Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Hair Care Essentials</h2>
                <p className="text-slate-500 mt-1">Conditioners, relaxers, styling gels &amp; leave-ins for every hair type</p>
              </div>
              <Link to="/products/hair">
                <Button variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50 rounded-full" data-testid="view-all-haircare-btn">
                  Shop Hair <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {hairCare.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Wholesale & Bulk */}
      {wholesale.length > 0 && (
        <section id="wholesale" className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white" data-testid="wholesale-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center ring-1 ring-emerald-400/40">
                    <Boxes className="h-4 w-4 text-emerald-300" />
                  </div>
                  <Badge className="bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/20 ring-1 ring-emerald-400/40 border-0">For restaurants &amp; caterers</Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold">Wholesale &amp; Bulk Boxes</h2>
                <p className="text-slate-300 mt-1">Cash-and-carry pricing on whole frozen-fish, meat &amp; poultry crates</p>
              </div>
              <Link to="/products/frozen-fish">
                <Button variant="outline" className="border-emerald-400/50 text-emerald-200 hover:bg-emerald-500/10 hover:text-emerald-100 rounded-full bg-transparent" data-testid="view-all-wholesale-btn">
                  Browse Bulk Range <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {wholesale.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Why Choose Gorton Superstore</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Your trusted destination for authentic ethnic groceries in Manchester</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Quality & Variety */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <Award className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Quality & Variety</h3>
              <p className="text-slate-600 leading-relaxed">
                Explore our wide range of premium Halal meat, fish, fresh exotic fruits and vegetables — all chosen for their exceptional quality and taste.
              </p>
            </div>

            {/* Trusted by Community */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Trusted by the Community</h3>
              <p className="text-slate-600 leading-relaxed">
                A favourite in Manchester. We're your go-to store for diverse and authentic ethnic groceries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Store Gallery */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-amber-100 text-amber-700 mb-3">Take a Look Inside</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Explore Our Store</h2>
            <p className="text-slate-600">From bulk rice and spices to fresh vegetables and ethnic products</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/baw15014_da038d02-30f2-4ac0-b1b0-b4b70a022090.jpg" 
                alt="Rice and Basmati section"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Rice & Grains</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/st0mhnr8_e7b6e04c-ab09-4b72-8784-8a355bfa6c19.jpg" 
                alt="Beverages and drinks section"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Beverages</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/2orl1xjc_e81e3dd7-ad4e-44b0-a632-b534e53ab3d6.jpg" 
                alt="Ethnic products - Milo, Peak Milk, Cerelac"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Ethnic Products</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/svr9re7k_a3d80760-ce00-4a0e-b7ed-db1e34b6c3be.jpg" 
                alt="Supermalt and bulk products"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Bulk Deals</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/43bh9sok_9b3c5008-4ec1-4415-a576-3f807a5085c9.jpg" 
                alt="Fresh vegetables - onions, garlic, ginger"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Fresh Vegetables</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/u9hv72nr_d379f786-a086-4528-80ac-9c9aef4b12a5.jpg" 
                alt="Spices and oils - Rajah, Palm Oil"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Spices & Oils</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/urjejdq9_IMG_6123.jpg" 
                alt="Halal meat counter"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Halal Meat</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/ekvd6d5k_IMG_6125.jpg" 
                alt="Fresh lamb leg"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Fresh Lamb</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/vbyhl7xq_IMG_6120.jpg" 
                alt="Poultry and fish counter"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Poultry & Fish</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/6nl6nqdw_IMG_6121.jpg" 
                alt="Fresh meat display"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Fresh Meat</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/wafdxb8t_IMG_6122.jpg" 
                alt="Butcher counter"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Butcher Counter</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/b83q0kx9_IMG_6133.jpg" 
                alt="Frozen fish - Pangasius steaks"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Frozen Fish</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/cnkora21_IMG_6134.jpg" 
                alt="Fresh mackerel and salmon"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Fresh Seafood</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/lt9dsygq_IMG_6136.jpg" 
                alt="Frozen oxtail and meat"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Frozen Oxtail</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/mgbh8ive_IMG_6139.jpg" 
                alt="Frozen meat packs"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Frozen Meat</p>
              </div>
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/5hol0hnq_9b3c5008-4ec1-4415-a576-3f807a5085c9.jpg" 
                alt="Fresh produce aisle"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <p className="text-white font-medium text-sm">Fresh Produce</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fresh African Vegetables */}
      <section className="py-16 sm:py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-emerald-600 text-white mb-3">Fresh Daily</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Fresh African Vegetables</h2>
            <p className="text-slate-600">Authentic African leaves for your traditional dishes</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="aspect-square rounded-xl overflow-hidden mb-4 bg-slate-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/fnecwsw7_IMG_6145.jpg" 
                  alt="Bitter Leaf"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-semibold text-slate-900">Bitter Leaf</h3>
              <p className="text-sm text-slate-500">Fresh & Healthy</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="aspect-square rounded-xl overflow-hidden mb-4 bg-slate-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/t0dyb11l_IMG_6142.jpg" 
                  alt="Ugu Leaf"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-semibold text-slate-900">Ugu Leaf</h3>
              <p className="text-sm text-slate-500">Pumpkin Leaves</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="aspect-square rounded-xl overflow-hidden mb-4 bg-slate-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/g6vypxq5_IMG_6143.jpg" 
                  alt="Okazi Leaf"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-semibold text-slate-900">Okazi Leaf</h3>
              <p className="text-sm text-slate-500">Wild Spinach</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="aspect-square rounded-xl overflow-hidden mb-4 bg-slate-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/6owfsu20_IMG_6144.jpg" 
                  alt="Oha Leaf"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-semibold text-slate-900">Oha Leaf</h3>
              <p className="text-sm text-slate-500">Ora Leaves</p>
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="aspect-square rounded-xl overflow-hidden mb-4 bg-slate-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/rkik4w8n_IMG_6146.jpg" 
                  alt="Scent Leaf"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-semibold text-slate-900">Scent Leaf</h3>
              <p className="text-sm text-slate-500">Nchanwu / Efirin</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/products/vegetables">
              <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6">
                Shop All Vegetables <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">Our Story</Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
              Bringing the World's Flavours to the North West
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              At Gorton Super Stores, we offer a wide range of West African, Caribbean, Asian and ethnic products, serving the North West of England with unmatched diversity.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              We have grown from a basic grocery supplier into a lively cultural centre, bringing the richness of West African, Caribbean, and Asian cuisine and culture to the heart of the North West.
            </p>
            <Link to="/products">
              <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6">
                Explore Our Products <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          {/* Store Image */}
          <div className="relative max-w-5xl mx-auto overflow-hidden rounded-2xl shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/00m66slg_Untitled%20design.png" 
              alt="Gorton Superstore - Cash & Carry and Off Licence"
              className="w-full object-cover"
              data-testid="store-image"
            />
            <div className="absolute bottom-4 right-4 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-lg">
              <p className="font-bold">Visit Us Today!</p>
              <p className="text-sm text-emerald-100">578 Hyde Road</p>
            </div>
          </div>

          {/* Cuisine Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="bg-emerald-600 rounded-2xl p-6 text-white text-center">
              <Heart className="h-8 w-8 mx-auto mb-3" />
              <h4 className="font-bold text-lg">West African</h4>
              <p className="text-emerald-100 text-sm">Authentic products</p>
            </div>
            <div className="bg-amber-500 rounded-2xl p-6 text-white text-center">
              <h4 className="font-bold text-lg">Caribbean</h4>
              <p className="text-amber-100 text-sm">Island flavours</p>
            </div>
            <div className="bg-red-600 rounded-2xl p-6 text-white text-center">
              <h4 className="font-bold text-lg">Asian</h4>
              <p className="text-red-100 text-sm">Exotic spices & more</p>
            </div>
            <div className="bg-slate-800 rounded-2xl p-6 text-white text-center">
              <h4 className="font-bold text-lg">Ethnic Foods</h4>
              <p className="text-slate-300 text-sm">Global selection</p>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Specials */}
      {specials.length > 0 && (
        <section className="py-16 sm:py-20 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
              <div>
                <Badge className="bg-red-600 text-white mb-2">This Week Only</Badge>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Weekly Specials</h2>
              </div>
              <Link to="/products?special=true">
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-100">
                  View All Specials <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {specials.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Need Fresh Halal Meat Today?
          </h2>
          <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
            Call our butcher directly to discuss your order or place a custom request. We're here to help!
          </p>
          <a
            href="tel:07404133860"
            className="inline-flex items-center gap-2 bg-white text-emerald-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-emerald-50 transition-colors"
            data-testid="cta-call-btn"
          >
            <Phone className="h-5 w-5" />
            Call 07404 133860
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
